import { useEffect, useState } from 'react';
import Icon from '@components/AppIcon';
import Button from '@components/ui/Button';
import Input from '@components/ui/Input';
import Select from '@components/ui/Select';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    inquiryType: '',
    preferredContact: '',
    message: '',
    urgency: '',
    preferredTime: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [csrfToken, setCsrfToken] = useState('');

  const inquiryTypes = [
    { value: 'appointment', label: 'Schedule Appointment' },
    { value: 'general', label: 'General Questions' },
    { value: 'insurance', label: 'Insurance & Billing' },
    { value: 'emergency', label: 'Emergency Consultation' },
    { value: 'second-opinion', label: 'Second Opinion' },
    { value: 'follow-up', label: 'Follow-up Care' }
  ];

  const contactPreferences = [
    { value: 'phone', label: 'Phone Call' },
    { value: 'email', label: 'Email' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'sms', label: 'SMS Text' }
  ];

  const urgencyLevels = [
    { value: 'routine', label: 'Routine (within 1 week)' },
    { value: 'soon', label: 'Soon (within 2-3 days)' },
    { value: 'urgent', label: 'Urgent (within 24 hours)' },
    { value: 'emergency', label: 'Emergency (immediate)' }
  ];

  const preferredTimes = [
    { value: 'morning', label: 'Morning (8AM - 12PM)' },
    { value: 'afternoon', label: 'Afternoon (12PM - 5PM)' },
    { value: 'evening', label: 'Evening (5PM - 8PM)' },
    { value: 'anytime', label: 'Anytime' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  
  try {
    const response = await fetch('/api/contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
      body: JSON.stringify(formData)
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.message || 'Failed to submit form');
    }

    setSubmitStatus('success');
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      inquiryType: '',
      preferredContact: '',
      message: '',
      urgency: '',
      preferredTime: ''
    });
  } catch (error) {
    console.error('Form submission error:', error);
    setSubmitStatus('error');
  } finally {
    setIsSubmitting(false);
  }
};

useEffect(() => {
  const token = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
  setCsrfToken(token);
}, []);

  return (
    <div className="relative py-12 px-4 md:px-0">
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="w-full h-full bg-gradient-to-br from-primary/10 via-white to-primary/5 rounded-3xl blur-xl"></div>
      </div>
      <div className="relative z-10 max-w-4xl mx-auto">
        <div className="bg-white/90 shadow-2xl rounded-3xl border border-primary/20 p-8 md:p-12">
          <div className="text-center mb-10">
            <div className="flex justify-center mb-4">
              <div className="bg-primary/10 rounded-full p-4 shadow-md">
                <Icon name="MessageCircle" size={32} className="text-primary" />
              </div>
            </div>
            <h2 className="text-3xl font-extrabold text-primary mb-2 tracking-tight">
              Send Us a Message
            </h2>
            <p className="text-lg text-text-secondary">
              Fill out the form below and we'll get back to you within 2 hours during business hours.
            </p>
          </div>

          {submitStatus === 'success' && (
            <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="CheckCircle" size={20} className="text-green-600" />
                <div>
                  <h4 className="font-medium text-green-800">Message Sent Successfully!</h4>
                  <p className="text-sm text-green-700">
                    We've received your message and will respond shortly.
                  </p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="AlertCircle" size={20} className="text-red-600" />
                <div>
                  <h4 className="font-medium text-red-800">Error Sending Message</h4>
                  <p className="text-sm text-red-700">
                    Please try again or call us directly at +254 718 202 020.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Input
                label="First Name"
                type="text"
                placeholder="Enter your first name"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                required
                className="bg-white/80 border border-primary/20 rounded-xl shadow-sm"
              />
              <Input
                label="Last Name"
                type="text"
                placeholder="Enter your last name"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                required
                className="bg-white/80 border border-primary/20 rounded-xl shadow-sm"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Input
                label="Email Address"
                type="email"
                placeholder="your.email@example.com"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
                className="bg-white/80 border border-primary/20 rounded-xl shadow-sm"
              />
              <Input
                label="Phone Number"
                type="tel"
                placeholder="+254 718 202 020"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                required
                className="bg-white/80 border border-primary/20 rounded-xl shadow-sm"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Select
                label="Type of Inquiry"
                placeholder="Select inquiry type"
                options={inquiryTypes}
                value={formData.inquiryType}
                onChange={(value) => handleInputChange('inquiryType', value)}
                required
                className="bg-white/80 border border-primary/20 rounded-xl shadow-sm"
              />
              <Select
                label="Preferred Contact Method"
                placeholder="How should we contact you?"
                options={contactPreferences}
                value={formData.preferredContact}
                onChange={(value) => handleInputChange('preferredContact', value)}
                required
                className="bg-white/80 border border-primary/20 rounded-xl shadow-sm"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Select
                label="Urgency Level"
                placeholder="How urgent is your inquiry?"
                options={urgencyLevels}
                value={formData.urgency}
                onChange={(value) => handleInputChange('urgency', value)}
                required
                className="bg-white/80 border border-primary/20 rounded-xl shadow-sm"
              />
              <Select
                label="Preferred Contact Time"
                placeholder="Best time to reach you"
                options={preferredTimes}
                value={formData.preferredTime}
                onChange={(value) => handleInputChange('preferredTime', value)}
                className="bg-white/80 border border-primary/20 rounded-xl shadow-sm"
              />
            </div>

            <div>
              <label className="block text-base font-semibold text-primary mb-2">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                className="w-full px-4 py-3 border border-primary/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-transparent resize-none bg-white/80 shadow-sm"
                rows={6}
                placeholder="Please describe your inquiry, symptoms, or questions in detail..."
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                required
              />
              <p className="text-xs text-text-secondary mt-2">
                Provide as much detail as possible to help us assist you better.
              </p>
            </div>

            <div className="bg-gradient-to-r from-primary/5 via-white to-primary/10 p-6 rounded-2xl border border-primary/10 shadow-md">
              <div className="flex items-start space-x-4">
                <Icon name="Info" size={24} className="text-accent mt-1 flex-shrink-0" />
                <div className="text-base text-text-secondary">
                  <p className="font-semibold text-primary mb-2">Privacy Notice</p>
                  <p>
                    Your information is protected under HIPAA regulations. We will only use your contact information to respond to your inquiry and provide requested services.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                type="submit"
                variant="default"
                size="xl"
                fullWidth
                loading={isSubmitting}
                iconName="Send"
                iconPosition="left"
                className="bg-gradient-to-r from-primary to-primary/80 text-white font-bold rounded-xl shadow-lg hover:from-primary/90 hover:to-primary/70 transition-all"
              >
                {isSubmitting ? 'Sending Message...' : 'Send Message'}
              </Button>

              <Button
                type="button"
                variant="outline"
                size="xl"
                iconName="Phone"
                iconPosition="left"
                onClick={() => window.location.href = 'tel:+254718202020'}
              >
                Call Instead
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;